import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import api from '../services/api';
import { maskTelefone, maskCep, maskCpfCnpj, formatNomeProprio, trimEspacos } from '../utils/masks';

type Step = 'cliente' | 'visita' | 'resumo';

export default function NovoOrcamentoPage() {
  const navigate = useNavigate();
  const [step, setStep] = useState<Step>('cliente');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Cliente
  const [clienteSearch, setClienteSearch] = useState('');
  const [clienteSelecionado, setClienteSelecionado] = useState<any>(null);
  const [novoCliente, setNovoCliente] = useState(false);
  const [clienteForm, setClienteForm] = useState({
    nome: '', telefone: '', email: '', cpfCnpj: '',
    cep: '', logradouro: '', numero: '', complemento: '', bairro: '', cidade: '', estado: 'SP',
  });

  // Visita
  const [dataVisita, setDataVisita] = useState('');
  const [dataValidade, setDataValidade] = useState('');
  const [observacoes, setObservacoes] = useState('');

  const { data: clientes = [] } = useQuery({
    queryKey: ['clientes-search', clienteSearch],
    queryFn: () => api.get('/clientes', { params: { search: clienteSearch } }).then(r => r.data),
    enabled: clienteSearch.length > 2,
  });

  async function handleSubmit() {
    setLoading(true);
    setError('');
    try {
      let clienteId = clienteSelecionado?.id;

      if (novoCliente) {
        const res = await api.post('/clientes', clienteForm);
        clienteId = res.data.id;
      }

      if (!clienteId) {
        setError('Selecione ou cadastre um cliente');
        setLoading(false);
        return;
      }

      const res = await api.post('/orcamentos', {
        clienteId,
        dataVisita: dataVisita ? new Date(dataVisita).toISOString() : null,
        dataValidade: dataValidade ? new Date(dataValidade).toISOString() : null,
        observacoesGerais: observacoes,
      });

      navigate(`/orcamentos/${res.data.id}`);
    } catch (e: any) {
      setError(e.response?.data?.error || 'Erro ao criar orçamento');
    } finally {
      setLoading(false);
    }
  }

  const stepLabels = ['1. Cliente', '2. Visita', '3. Confirmar'];
  const stepKeys: Step[] = ['cliente', 'visita', 'resumo'];
  const stepIndex = stepKeys.indexOf(step);

  return (
    <div className="max-w-lg mx-auto space-y-6">
      <div className="flex items-center gap-3">
        <button onClick={() => navigate('/orcamentos')} className="text-gray-500 hover:text-gray-700">←</button>
        <h1 className="text-xl font-bold">Novo Orçamento</h1>
      </div>

      {/* Steps indicator */}
      <div className="flex items-center gap-2">
        {stepLabels.map((label, i) => (
          <React.Fragment key={label}>
            <button
              onClick={() => i < stepIndex ? setStep(stepKeys[i]) : undefined}
              className={`flex-1 text-center py-2 rounded-lg text-sm font-medium transition-colors ${
                i === stepIndex ? 'bg-primary-600 text-white' :
                i < stepIndex ? 'bg-primary-100 text-primary-700 cursor-pointer' :
                'bg-gray-100 text-gray-400'
              }`}
            >
              {label}
            </button>
            {i < stepLabels.length - 1 && <div className="text-gray-300">›</div>}
          </React.Fragment>
        ))}
      </div>

      {/* Passo 1: Cliente */}
      {step === 'cliente' && (
        <div className="card space-y-4">
          <h2 className="font-semibold">Dados do Cliente</h2>

          <div className="flex gap-2">
            <button
              onClick={() => setNovoCliente(false)}
              className={`flex-1 py-2 rounded-lg text-sm font-medium border ${!novoCliente ? 'bg-primary-600 text-white border-primary-600' : 'border-gray-300'}`}
            >
              Buscar existente
            </button>
            <button
              onClick={() => setNovoCliente(true)}
              className={`flex-1 py-2 rounded-lg text-sm font-medium border ${novoCliente ? 'bg-primary-600 text-white border-primary-600' : 'border-gray-300'}`}
            >
              Novo cliente
            </button>
          </div>

          {!novoCliente ? (
            <div>
              <label className="label">Buscar por nome, telefone ou email</label>
              <input
                className="input"
                value={clienteSearch}
                onChange={e => setClienteSearch(e.target.value)}
                placeholder="Digite pelo menos 3 caracteres..."
              />
              {clientes.length > 0 && !clienteSelecionado && (
                <div className="mt-2 border rounded-lg overflow-hidden">
                  {clientes.map((c: any) => (
                    <button
                      key={c.id}
                      onClick={() => { setClienteSelecionado(c); setClienteSearch(c.nome); }}
                      className="w-full text-left px-3 py-2 hover:bg-gray-50 border-b last:border-0"
                    >
                      <div className="font-medium text-sm">{c.nome}</div>
                      <div className="text-xs text-gray-500">{c.telefone} · {c.email}</div>
                    </button>
                  ))}
                </div>
              )}
              {clienteSelecionado && (
                <div className="mt-2 p-3 bg-primary-50 rounded-lg flex justify-between items-center">
                  <div>
                    <div className="font-medium text-sm">{clienteSelecionado.nome}</div>
                    <div className="text-xs text-gray-500">{clienteSelecionado.telefone}</div>
                  </div>
                  <button onClick={() => { setClienteSelecionado(null); setClienteSearch(''); }} className="text-red-500 text-sm">✕</button>
                </div>
              )}
            </div>
          ) : (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div className="col-span-2">
                  <label className="label">Nome *</label>
                  <input className="input" value={clienteForm.nome} onChange={e => setClienteForm({...clienteForm, nome: e.target.value})} required />
                </div>
                <div>
                  <label className="label">Telefone *</label>
                  <input className="input" value={clienteForm.telefone} onChange={e => setClienteForm({...clienteForm, telefone: maskTelefone(e.target.value)})} placeholder="(11) 99999-9999" type="tel" inputMode="numeric" maxLength={15} required />
                </div>
                <div>
                  <label className="label">Email</label>
                  <input className="input" type="email" value={clienteForm.email} onChange={e => setClienteForm({...clienteForm, email: e.target.value})} />
                </div>
                <div>
                  <label className="label">CPF/CNPJ</label>
                  <input className="input" value={clienteForm.cpfCnpj} onChange={e => setClienteForm({...clienteForm, cpfCnpj: maskCpfCnpj(e.target.value)})} placeholder="000.000.000-00" inputMode="numeric" maxLength={18} />
                </div>
                <div>
                  <label className="label">CEP</label>
                  <input className="input" value={clienteForm.cep} onChange={e => setClienteForm({...clienteForm, cep: maskCep(e.target.value)})} placeholder="00000-000" inputMode="numeric" maxLength={9} />
                </div>
                <div className="col-span-2">
                  <label className="label">Endereço</label>
                  <input className="input" value={clienteForm.logradouro} onChange={e => setClienteForm({...clienteForm, logradouro: formatNomeProprio(e.target.value)})} onBlur={e => setClienteForm({...clienteForm, logradouro: trimEspacos(e.target.value)})} placeholder="Rua, Av..." />
                </div>
                <div>
                  <label className="label">Número</label>
                  <input className="input" value={clienteForm.numero} onChange={e => setClienteForm({...clienteForm, numero: e.target.value})} />
                </div>
                <div>
                  <label className="label">Complemento</label>
                  <input className="input" value={clienteForm.complemento} onChange={e => setClienteForm({...clienteForm, complemento: formatNomeProprio(e.target.value)})} onBlur={e => setClienteForm({...clienteForm, complemento: trimEspacos(e.target.value)})} />
                </div>
                <div>
                  <label className="label">Bairro</label>
                  <input className="input" value={clienteForm.bairro} onChange={e => setClienteForm({...clienteForm, bairro: formatNomeProprio(e.target.value)})} onBlur={e => setClienteForm({...clienteForm, bairro: trimEspacos(e.target.value)})} />
                </div>
                <div>
                  <label className="label">Cidade</label>
                  <input className="input" value={clienteForm.cidade} onChange={e => setClienteForm({...clienteForm, cidade: formatNomeProprio(e.target.value)})} onBlur={e => setClienteForm({...clienteForm, cidade: trimEspacos(e.target.value)})} />
                </div>
              </div>
            </div>
          )}

          <button
            onClick={() => setStep('visita')}
            disabled={!novoCliente ? !clienteSelecionado : !clienteForm.nome || !clienteForm.telefone}
            className="btn-primary w-full"
          >
            Próximo
          </button>
        </div>
      )}

      {/* Passo 2: Visita */}
      {step === 'visita' && (
        <div className="card space-y-4">
          <h2 className="font-semibold">Informações da Visita</h2>

          <div>
            <label className="label">Data da Visita / Medição</label>
            <input type="datetime-local" className="input" value={dataVisita} onChange={e => setDataVisita(e.target.value)} />
            <p className="text-xs text-gray-400 mt-1">Quando vai ao local para medir e avaliar</p>
          </div>

          <div>
            <label className="label">Validade do Orçamento</label>
            <input type="date" className="input" value={dataValidade} onChange={e => setDataValidade(e.target.value)} />
            <p className="text-xs text-gray-400 mt-1">Por quanto tempo o orçamento é válido</p>
          </div>

          <div>
            <label className="label">Observações Gerais</label>
            <textarea
              className="input resize-none"
              rows={3}
              value={observacoes}
              onChange={e => setObservacoes(e.target.value)}
              placeholder="Alguma informação relevante sobre o cliente ou local..."
            />
          </div>

          <div className="flex gap-3">
            <button onClick={() => setStep('cliente')} className="btn-secondary flex-1">Voltar</button>
            <button onClick={() => setStep('resumo')} className="btn-primary flex-1">Próximo</button>
          </div>
        </div>
      )}

      {/* Passo 3: Resumo */}
      {step === 'resumo' && (
        <div className="card space-y-4">
          <h2 className="font-semibold">Confirmar Orçamento</h2>

          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-gray-500">Cliente</span>
              <span className="font-medium">{clienteSelecionado?.nome || clienteForm.nome}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">Telefone</span>
              <span>{clienteSelecionado?.telefone || clienteForm.telefone}</span>
            </div>
            {dataVisita && (
              <div className="flex justify-between">
                <span className="text-gray-500">Visita</span>
                <span>{new Date(dataVisita).toLocaleString('pt-BR')}</span>
              </div>
            )}
            {dataValidade && (
              <div className="flex justify-between">
                <span className="text-gray-500">Validade</span>
                <span>{new Date(dataValidade).toLocaleDateString('pt-BR')}</span>
              </div>
            )}
          </div>

          <p className="text-xs text-gray-500 bg-blue-50 p-3 rounded-lg">
            Após criar o orçamento, você poderá adicionar as janelas com medidas, tipos de persiana e fotos.
          </p>

          {error && <p className="text-red-600 text-sm">{error}</p>}

          <div className="flex gap-3">
            <button onClick={() => setStep('visita')} className="btn-secondary flex-1">Voltar</button>
            <button onClick={handleSubmit} disabled={loading} className="btn-primary flex-1">
              {loading ? 'Criando...' : 'Criar Orçamento'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
